import { MutableRefObject, useEffect } from 'react'
import styled from '@emotion/styled'
import { useInstructor } from 'shared/state/useAuth'
import { H6, Text } from 'library/atoms/Typography'
import { definedNotNull } from 'shared/presenters/presenter'
import useTranslation from 'next-translate/useTranslation'
import { gql, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import { ON_SERVER } from 'shared/enums/environment'
import { OutletPresenter } from 'shared/presenters'
import determineColorForHover from 'helpers/determineColorForHover'
import SidebarNavList from './SidebarNavList'
import Icon from 'shared/library/atoms/Icon'
import formatSidebarNavLinks, {
  SidebarJSON
} from 'helpers/formatSidebarNavLinks'
import { useSidebar } from 'state/useSidebar'
import { SIDEBAR_ID } from 'enums/sidebar'
import ProfileImage from 'library/atoms/ProfileImage'
import determineSidebarBreadcrumbForCurrentPath from 'helpers/determineSidebarBreadcrumbForCurrentPath'

export interface SidebarProps {
  /**
   * The reference is used in combination with the Header component to trigger
   * click away functions.
   *
   * We can't use "ref" because it is reserved to React.
   */
  reference?: MutableRefObject<HTMLElement | null>
}

const Sidebar = ({ reference }: SidebarProps) => {
  const { t } = useTranslation('common')
  const { query, asPath } = useRouter()
  const {
    visibleNavList: { current, previous },
    updateVisibleNavList,
    sidebarIsVisible
  } = useSidebar()
  const outletSlug = query.outletSlug as string
  const { instructor } = useInstructor()
  const outlet = instructor?.instructors_outlets
    ?.filter(outlets => outlets?.outlet?.slug === outletSlug)
    .map(outlets => new OutletPresenter(definedNotNull(outlets?.outlet)))[0]

  const { data: sidebarJSON, loading: loadingSidebarJSON } =
    useQuery<SidebarJSON>(FETCH_INSTRUCTOR_SIDEBAR, {
      variables: {
        outletSlug
      },
      skip: ON_SERVER || !outletSlug,
      ssr: ON_SERVER
    })

  const { defaultMenu, instructorMenu } = formatSidebarNavLinks(sidebarJSON)

  /**
   * Update the visible navlist if the page renders in a page
   * that belongs to a location found in the navigation.
   */
  useEffect(() => {
    if (loadingSidebarJSON) return

    const menus = determineSidebarBreadcrumbForCurrentPath({
      sidebarMenus: [defaultMenu, instructorMenu],
      currentPath: asPath,
      relevantCurrentPath: asPath.split(outletSlug)[1]
    })

    if (!menus) return

    updateVisibleNavList(menus)
    // TODO #6367 - Fix ESLint - hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSidebarJSON])

  const profileSrc = instructor?.profile_picture

  return (
    <StyledSidebar
      role="navigation"
      aria-label={t('sidebar')}
      isVisible={!!sidebarIsVisible}
      // Declare id for aria-controls that is used by the hamburger menu
      id={SIDEBAR_ID}
      ref={reference}
    >
      <StyledSidebarNavListWrapper role="presentation">
        <SidebarNavList
          title={defaultMenu.name}
          links={defaultMenu?.collection}
        />
        {/**
         * The instructor menu is rendered as an extra menu because the animation interaction demands
         * this menu to look as a subnav of whatever navItem the user is viewing at the moment.
         * In other words, if the user clicks to see the instructor menu the default nav will need to
         * slide to the left.
         */}
        {instructorMenu && (
          <SidebarNavList
            title={instructorMenu.name}
            links={instructorMenu?.collection}
          />
        )}
      </StyledSidebarNavListWrapper>
      {instructor && instructorMenu && (
        <StyledInstructorButton
          onClick={() => updateVisibleNavList(instructorMenu.name)}
          disabled={
            !!(
              current === instructorMenu.name ||
              previous?.some(name => name === instructorMenu.name)
            )
          }
        >
          <StyledProfileImage>
            {profileSrc ? (
              <ProfileImage src={profileSrc} alt={t('profile-image')} />
            ) : (
              <Icon name="user" color="grey" size={24} />
            )}
          </StyledProfileImage>
          <StyledProfileText>
            <H6>{instructor.name()}</H6>
            {outlet && <Text fontStyle="p5">{outlet.name}</Text>}
          </StyledProfileText>
        </StyledInstructorButton>
      )}
    </StyledSidebar>
  )
}

export default Sidebar

const FETCH_INSTRUCTOR_SIDEBAR = gql`
  query BusinessSidebarFetchInstructorSidebar($outletSlug: String!) {
    Business_InstructorSidebar(outlet_slug: $outletSlug) {
      items
    }
  }
`

const StyledSidebar = styled.nav<{ isVisible?: boolean }>(
  ({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.mixins.pxSpread([theme.space[4], 0]),
    background: theme.colors.background.primary,
    width: '100%',
    position: 'absolute',
    zIndex: theme.zIndex.sidebar,
    height: `calc(100% - ${theme.layout.headerHeight}px)`,
    transition: `all ${theme.transitionTime[1]}s ease`,
    [theme.mediaQueries.sm]: {
      maxWidth: theme.layout.sidebarWidth
    }
  }),
  // Styles for determining if the sidebar is visible
  ({ isVisible, theme }) => ({
    left: isVisible ? 0 : '-100%',
    visibility: isVisible ? 'visible' : 'hidden',
    // Reset style to make the sidebar always visible on large screens
    [theme.mediaQueries.lg]: {
      visibility: 'visible',
      position: 'unset',
      height: 'unset',
      left: 'unset'
    }
  })
)

const StyledProfileImage = styled.div(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: '1 0 auto',
  borderRadius: theme.radii[2],
  boxShadow: theme.shadows[0],
  background: theme.colors.white,
  width: 24,
  height: 24,
  flexGrow: 0
}))

const StyledProfileText = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: theme.space[2],
  overflow: 'hidden', // Need it for ellipsis
  '> *': {
    ...theme.mixins.ellipsisWidthClamp()
  }
}))

const StyledSidebarNavListWrapper = styled.div(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  flex: '1 0 0',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: theme.space[3]
}))

const StyledInstructorButton = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  padding: theme.mixins.pxSpread([
    // Add 2px to look good on :focus-visible
    2, 0, 0, 2
  ]),
  margin: theme.mixins.pxSpread([0, theme.space[5]]),
  textAlign: 'left',
  color: theme.colors.text.secondary,
  transition: `${theme.transitionTime[1]}s ease`,
  ':hover': {
    color: determineColorForHover(theme.colors.text.secondary)
  }
}))
